import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { brandLabel, chromeColorFor, type Branding, type BrandingStore } from '@lemontaigu/platform';

/**
 * The branding context — the tenant's name and chrome colour, fetched once per
 * session and injected into the shell (docs/UI-BRANDING.md B4–B6).
 *
 * WHAT IT REPLACES. Six literal « Le Montaigu » strings across five apps, and
 * one hex value written out 144 times. Both were invisible while there was one
 * tenant, and the day a second one signs in, a compiled organisation name is a
 * lie rendered as fact in their own header.
 *
 * ── How a database row paints a header (the mechanic, B6) ───────────────────
 *
 * The stylesheets are static files compiled at build time, and CSS cannot read
 * a table. So the stylesheet stops holding a VALUE and holds a REFERENCE —
 * `background: var(--chrome-navy, #232347)` — and this provider sets the custom
 * property once the tenant is known. The component declares a slot; the context
 * fills it. That is B4's principle in three lines.
 *
 * ── Why the fallback in the CSS is the brand blue, and why that matters ─────
 *
 * The page paints BEFORE the fetch returns. With a fallback that differed, every
 * cold load would flash one colour and swap to another. Because the compiled
 * fallback IS what the only tenant declares, the two paints are identical and
 * nothing moves. A second tenant with a different blue is the case that would
 * flash — and the localStorage hydration below is what answers it.
 *
 * ── The cache is a paint optimisation, never a source of truth ──────────────
 *
 * Last-known branding is hydrated synchronously so the first render already has
 * a name and a colour. It is overwritten by the fetch on every session. The one
 * visible artefact: signing in as a DIFFERENT tenant shows the previous
 * tenant's name for the length of one fetch. Recorded rather than hidden — it
 * is the same trade every "remember the last state" cache makes, and the fix
 * (clear on sign-out) belongs with whatever first has two tenants to confuse.
 */

interface BrandingContextValue {
  branding: Branding | null;
  /** False once the first fetch has settled, successfully or not. */
  loading: boolean;
  /** Non-null if the fetch failed. Branding falls back; nothing else breaks. */
  error: string | null;
  /** `« PMS — Le Montaigu »` — app name constant, organisation name data (B2).
   *  Returns the app name ALONE while the tenant is unknown. */
  label(app: string): string;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

const CACHE_KEY = 'platform.branding';
/** The custom property every shell stylesheet references. One name, because
 *  one value — a second name would be a second place to change it. */
const CHROME_VAR = '--chrome-navy';

function readCache(): Branding | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const b = parsed as Partial<Branding>;
    // Shape-check rather than trust: localStorage is user-writable, and this
    // value ends up in a CSS property and a header. `chrome` is re-validated
    // by the same rule the adapter applies.
    if (typeof b.tenantCode !== 'string' || typeof b.labelFr !== 'string') return null;
    const chrome: Record<string, string> = {};
    for (const [k, v] of Object.entries(b.chrome ?? {})) {
      if (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)) chrome[k] = v;
    }
    return { tenantCode: b.tenantCode, labelFr: b.labelFr, chrome };
  } catch {
    return null;
  }
}

function writeCache(b: Branding | null): void {
  try {
    if (b) window.localStorage.setItem(CACHE_KEY, JSON.stringify(b));
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {
    // A cache that cannot be written is a flash on the next load, not an error.
  }
}

export interface BrandingProviderProps {
  /** Supplied by the app's composition root. Never imported here — this file
   *  names no infrastructure, same contract as the auth gates. */
  store: BrandingStore;
  /** This app's slug, used to pick its entry from the tenant's brand map
   *  (`pms`, `reception`, `admin`, `staff`). Falls back to `default`. */
  app: string;
  children: ReactNode;
}

export function BrandingProvider({ store, app, children }: BrandingProviderProps) {
  // Hydrated in the INITIALISER, not an effect: an effect would run after the
  // first paint, which is the flash this exists to avoid.
  const [branding, setBranding] = useState<Branding | null>(readCache);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    store
      .mine()
      .then((b) => {
        if (cancelled) return;
        setBranding(b);
        writeCache(b);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Keep whatever is on screen. Branding is cosmetic: refusing to render
        // the app because its header colour could not be read would be a far
        // worse outcome than rendering it in the compiled fallback.
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  // The injection itself. Set on the ROOT element rather than a wrapper so it
  // reaches every stylesheet in the document — including the shell's, which is
  // rendered by a sibling tree in some apps.
  useEffect(() => {
    const colour = chromeColorFor(branding, app);
    const root = document.documentElement;
    if (colour) root.style.setProperty(CHROME_VAR, colour);
    // NOT removed on unmount when absent: leaving the property unset is what
    // lets the CSS fallback apply, and clearing it on every render would fight
    // the paint. Absent means "the tenant states nothing".
    else root.style.removeProperty(CHROME_VAR);
  }, [branding, app]);

  const value = useMemo<BrandingContextValue>(
    () => ({ branding, loading, error, label: (a: string) => brandLabel(a, branding) }),
    [branding, loading, error],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within <BrandingProvider>');
  return ctx;
}

/**
 * The header's brand line — `« PMS — Le Montaigu »`.
 *
 * Four lines, shared rather than repeated, because repeating it four times is
 * the exact shape of the problem being fixed: five apps each held their own
 * copy of this string, and five copies is five places to miss when the name is
 * not « Le Montaigu » any more.
 *
 * `app` is the constant half and is passed in — the component cannot know
 * whether it is rendering the PMS or the back office, and should not guess.
 */
export function AppBrand({ app }: { app: string }) {
  const { label } = useBranding();
  return <span className="app-brand">{label(app)}</span>;
}
